import { type ReactNode, createContext, useContext, useMemo, useState } from "react";

function todayIso(): string {
  return new Date().toISOString().slice(0, 10);
}

// The shared time-machine state: the `asOf` date drives every view (map,
// calendar, hours) so they stay in sync ("show me everyone on Aug 12").
interface ViewState {
  asOf: string;
  setAsOf: (d: string) => void;
  shift: (days: number) => void;
  reset: () => void;
}

const Ctx = createContext<ViewState | null>(null);

export function useViewState(): ViewState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useViewState must be used within <ViewStateProvider>");
  return ctx;
}

export function ViewStateProvider({ children }: { children: ReactNode }) {
  const [asOf, setAsOf] = useState(todayIso());
  const value = useMemo<ViewState>(
    () => ({
      asOf,
      setAsOf,
      shift: (days: number) => {
        const d = new Date(`${asOf}T00:00:00Z`);
        d.setUTCDate(d.getUTCDate() + days);
        setAsOf(d.toISOString().slice(0, 10));
      },
      reset: () => setAsOf(todayIso()),
    }),
    [asOf],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}
